import {
  SessionSchema,
  SessionsResponseSchema,
  FeedbackInvitationResultSchema,
  LocationsResponseSchema,
  CurrentKioskDeviceSchema,
  type CreateFeedbackInvitation,
  type CurrentKioskDevice,
  type LocationEntity,
  type Session,
} from "shared"
import { parseApiError } from "@rawa/api-client/ApiResponseError"
import { fingerprintHeader } from "./utils/fingerprint"

export async function fetchLocations(): Promise<LocationEntity[]> {
  const res = await fetch("/api/locations")
  if (!res.ok) throw await parseApiError(res)
  return LocationsResponseSchema.parse(await res.json()).locations
}

// This tablet's own device record. A 401 here means the credential is missing or
// revoked, which is why callers use it as the enrolment check as well as for the
// locations it resolves to display names.
export async function fetchKioskDevice(): Promise<CurrentKioskDevice> {
  const res = await fetch("/api/kiosk/device", {
    headers: await fingerprintHeader(),
  })
  if (!res.ok) throw await parseApiError(res)
  return CurrentKioskDeviceSchema.parse(await res.json())
}

// Moves an already-enrolled device to another set of locations; enrolDevice is
// first-time setup. PUT because it replaces the locations on a device that
// already exists.
export async function setLocation(locationIds: string[]): Promise<void> {
  const res = await fetch("/api/kiosk/location", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ locationIds }),
  })
  if (!res.ok) throw await parseApiError(res)
}

// First-time setup (ADR-025); setLocation moves an already-enrolled device.
export async function enrolDevice(locationIds: string[]): Promise<void> {
  const res = await fetch("/api/kiosk/enrol", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(await fingerprintHeader()),
    },
    body: JSON.stringify({ locationIds }),
  })
  if (!res.ok) throw await parseApiError(res)
}

export async function fetchSessions(): Promise<Session[]> {
  const res = await fetch("/api/kiosk/sessions")
  if (!res.ok) throw await parseApiError(res)
  return SessionsResponseSchema.parse(await res.json()).sessions
}

export async function fetchSession(id: string): Promise<Session> {
  const res = await fetch(`/api/kiosk/sessions/${id}`)
  if (!res.ok) throw await parseApiError(res)
  return SessionSchema.parse(await res.json())
}

export async function postFeedbackInvitation(
  sessionId: string,
  contactId: string,
): Promise<string> {
  const res = await fetch("/api/feedback-invitations", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      sessionId,
      contactId,
    } satisfies CreateFeedbackInvitation),
  })
  if (!res.ok) throw await parseApiError(res)
  return FeedbackInvitationResultSchema.parse(await res.json()).id
}
